import {
  ApolloCache,
  gql,
  QueryHookOptions,
  useApolloClient,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import { GET_BRAND_GROUPS_QUERY } from 'src/graphql/entities/brand-group';
import { QueryVariables, Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { BrandGroupRaw, BrandRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';

export const BRAND_FIELDS = gql`
  fragment BrandFields on Brand {
    id
    brandId
    name
    logo
  }
`;

export const BRAND_WITH_SUPPORT_FIELDS = gql`
  fragment BrandWithSupportFields on Brand {
    id
    brandId
    partnerId
    name
    displayName
    logo
    brandGroupId
    brandGroup {
      id
      brandGroupName
      fwdPhoneSupport
      fwdPhonePublished
      fwdPhonePublishedExt
      supportEmail
      membershipEmail
    }
  }
`;

const BRAND_MUTATION_RESULT_FIELDS = gql`
  fragment BrandMutationResultFields on Brand {
    id
    brandId
    partnerId
    name
    displayName
    brandGroupId
  }
`;

const GET_BRANDS_QUERY = gql`
  query GetBrand($params: QueryParams) @entity(name: "Brand") {
    getBrand(queryParams: $params) {
      records {
        ...BrandFields
      }
    }
  }
  ${BRAND_FIELDS}
`;

const GET_BRANDS_WITH_SUPPORT_QUERY = gql`
  query GetBrandsWithSupport($params: QueryParams) @entity(name: "Brand") {
    getBrand(queryParams: $params) {
      records {
        ...BrandWithSupportFields
      }
    }
  }
  ${BRAND_WITH_SUPPORT_FIELDS}
`;

const GET_BRAND_SITES_COUNT_QUERY = gql`
  query GetBrandSitesCount($params: QueryParamsWithSearch, $skipTotalRecords: Boolean = false)
  @entity(name: "Address") {
    getAddress(queryParams: $params) {
      metadata {
        totalRecords @skip(if: $skipTotalRecords)
      }
    }
  }
`;

const CREATE_BRAND_MUTATION = gql`
  mutation CreateBrand($data: BrandDataInput) {
    createBrand(data: $data) {
      ...BrandMutationResultFields
    }
  }
  ${BRAND_MUTATION_RESULT_FIELDS}
`;

const UPDATE_BRAND_MUTATION = gql`
  mutation UpdateBrand($conditions: [ConditionInput], $data: BrandDataInput) {
    updateBrand(conditions: $conditions, data: $data) {
      ...BrandMutationResultFields
    }
  }
  ${BRAND_MUTATION_RESULT_FIELDS}
`;

const DELETE_BRAND_WITH_RELATED_DATA_MUTATION = gql`
  mutation DeleteBrandWithRelatedData($params: DeleteBrandWithRelatedDataInput!) {
    deleteBrandWithRelatedData(params: $params) {
      brandId
      success
      message
    }
  }
`;

interface GetBrands {
  getBrand: Records<BrandRaw>;
}

export type BrandWithSupport = Omit<BrandRaw, 'brandGroup'> & {
  brandGroup?: Pick<
    BrandGroupRaw,
    | 'id'
    | 'brandGroupName'
    | 'fwdPhoneSupport'
    | 'fwdPhonePublished'
    | 'fwdPhonePublishedExt'
    | 'supportEmail'
    | 'membershipEmail'
  > | null;
};

interface GetBrandsWithSupport {
  getBrand: Records<BrandWithSupport>;
}

interface GetBrandSitesCount {
  getAddress: {
    metadata?: { totalRecords?: number } | null;
  };
}

type BrandMutationResult = Pick<
  BrandRaw,
  'id' | 'brandId' | 'partnerId' | 'name' | 'displayName' | 'brandGroupId'
>;

interface CreateBrandInput {
  partnerId: number;
  name: string;
  displayName: string;
}

interface UpdateBrandInput {
  brandId: string;
  name?: string;
  displayName?: string;
}

interface DeleteBrandResult {
  brandId: string;
  success: boolean;
  message?: string | null;
}

export type { DeleteBrandResult };

const brandListQueryVars = (partnerId: number | null | undefined) => ({
  params: {
    filters: isNullish(partnerId)
      ? []
      : [{ attribute: 'partnerId', operator: '=', value: String(partnerId) }],
    sorting: { attribute: 'name' },
  },
});

const removeBrandFromListCache = (
  cache: ApolloCache<unknown>,
  partnerId: number | null | undefined,
  brandId: string,
) => {
  cache.updateQuery<GetBrandsWithSupport, QueryVariables>(
    {
      query: GET_BRANDS_WITH_SUPPORT_QUERY,
      variables: brandListQueryVars(partnerId) as QueryVariables,
    },
    (existing) => {
      if (!existing) return undefined;
      return {
        getBrand: {
          ...existing.getBrand,
          records: existing.getBrand.records.filter((r) => r.brandId !== brandId),
        },
      };
    },
  );
};

export const useBrands = (
  partnerId?: number | null,
  options?: QueryHookOptions<GetBrands> & { desc?: boolean },
) => {
  const isMultitenancyMode = useGlobalStore.use.isMultiTenancyMode() && isNullish(partnerId);
  const filters = [];
  // Query brands for all partners in multitenancy mode.
  if (!isMultitenancyMode) {
    filters.push({
      attribute: 'partnerId',
      operator: '=',
      value: String(partnerId),
    });
  }

  const { data, ...rest } = useQuery<GetBrands, QueryVariables>(GET_BRANDS_QUERY, {
    ...options,
    skip: isNullish(partnerId) && !isMultitenancyMode,
    variables: {
      params: {
        filters,
        sorting: { attribute: 'name', desc: options?.desc },
      },
    },
  });

  const brands = useMemo(() => data?.getBrand.records || [], [data?.getBrand.records]);

  return { brands, ...rest };
};

export const useBrandsWithSupport = (
  partnerId?: number | null,
  options?: QueryHookOptions<GetBrandsWithSupport>,
) => {
  const isMultitenancyMode = useGlobalStore.use.isMultiTenancyMode() && isNullish(partnerId);
  const filters = [];
  if (!isMultitenancyMode) {
    filters.push({
      attribute: 'partnerId',
      operator: '=',
      value: String(partnerId),
    });
  }

  const { data, ...rest } = useQuery<GetBrandsWithSupport, QueryVariables>(
    GET_BRANDS_WITH_SUPPORT_QUERY,
    {
      ...options,
      skip: isNullish(partnerId) && !isMultitenancyMode,
      variables: {
        params: {
          filters,
          sorting: { attribute: 'name' },
        },
      },
    },
  );

  const brands = useMemo(() => data?.getBrand.records || [], [data?.getBrand.records]);

  return { brands, ...rest };
};

export const useBrandSitesCount = (
  partnerId: number | null | undefined,
  brandId: string | null | undefined,
) => {
  const { data, loading } = useQuery<GetBrandSitesCount, QueryVariables>(
    GET_BRAND_SITES_COUNT_QUERY,
    {
      skip: isNullish(partnerId) || isNullish(brandId),
      variables: {
        params: {
          filters: [
            { attribute: 'partnerId', operator: '=', value: String(partnerId) },
            { attribute: 'brandId', operator: '=', value: String(brandId) },
          ],
          limit: 0,
        },
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  return {
    sitesCount: data?.getAddress?.metadata?.totalRecords ?? 0,
    loading,
  };
};

type BrandGroupLookupQuery = {
  getBrandGroup: Records<Pick<BrandGroupRaw, 'brandGroupId'>>;
};

export const useCreateBrand = () => {
  const apolloClient = useApolloClient();
  const { showErrorAlert } = useNotifications();
  const [mutate, { loading }] = useMutation<{ createBrand: BrandMutationResult[] }>(
    CREATE_BRAND_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong creating the brand, please try again');
      },
    },
  );

  const createBrand = useCallback(
    async (input: CreateBrandInput): Promise<BrandMutationResult | null> => {
      // attaching the first brand group available for the partner
      let brandGroupId: string | null = null;
      try {
        const { data } = await apolloClient.query<BrandGroupLookupQuery, QueryVariables>({
          query: GET_BRAND_GROUPS_QUERY,
          variables: {
            params: {
              filters: [
                { attribute: 'partnerId', operator: '=', value: String(input.partnerId) },
              ],
              sorting: { attribute: 'brandGroupName' },
            },
          },
          fetchPolicy: 'cache-first',
        });
        brandGroupId = data?.getBrandGroup?.records?.[0]?.brandGroupId ?? null;
      } catch (lookupError) {
        console.warn('Could not resolve brand group for new segment', lookupError);
      }

      const result = await mutate({
        variables: {
          data: {
            partnerId: input.partnerId,
            name: input.name,
            displayName: input.displayName,
            ...(brandGroupId ? { brandGroupId } : {}),
          },
        },
        refetchQueries: [
          {
            query: GET_BRANDS_WITH_SUPPORT_QUERY,
            variables: brandListQueryVars(input.partnerId),
          },
        ],
        awaitRefetchQueries: true,
      });
      return result.data?.createBrand?.[0] ?? null;
    },
    [apolloClient, mutate],
  );

  return { createBrand, loading };
};

export const useUpdateBrand = () => {
  const { showErrorAlert } = useNotifications();
  const [mutate, { loading }] = useMutation<{ updateBrand: BrandMutationResult[] }>(
    UPDATE_BRAND_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        showErrorAlert('Something went wrong updating the brand, please try again');
      },
    },
  );

  const updateBrand = useCallback(
    async (input: UpdateBrandInput): Promise<BrandMutationResult | null> => {
      const data: { name?: string; displayName?: string } = {};
      if (input.name !== undefined) data.name = input.name;
      if (input.displayName !== undefined) data.displayName = input.displayName;
      const result = await mutate({
        variables: {
          conditions: [{ attribute: 'brandId', operator: '=', value: input.brandId }],
          data,
        },
      });
      return result.data?.updateBrand?.[0] ?? null;
    },
    [mutate],
  );

  return { updateBrand, loading };
};

export const useDeleteBrandWithRelatedData = () => {
  const { showErrorAlert } = useNotifications();
  const [mutate, { loading }] = useMutation<{
    deleteBrandWithRelatedData: DeleteBrandResult;
  }>(DELETE_BRAND_WITH_RELATED_DATA_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Something went wrong deleting the brand, please try again');
    },
  });

  const deleteBrand = useCallback(
    async (params: { brandId: string; partnerId: number }): Promise<DeleteBrandResult> => {
      const result = await mutate({
        variables: { params },
        update: (cache, { data }) => {
          if (!data?.deleteBrandWithRelatedData?.success) return;
          removeBrandFromListCache(cache, params.partnerId, params.brandId);
          cache.evict({ id: cache.identify({ __typename: 'Brand', id: params.brandId }) });
          cache.gc();
        },
      });
      return (
        result.data?.deleteBrandWithRelatedData ?? {
          brandId: params.brandId,
          success: false,
          message: null,
        }
      );
    },
    [mutate],
  );

  return { deleteBrand, loading };
};

export function useBrandOptions(
  partnerId?: number | null,
  options?: QueryHookOptions<GetBrands> & { desc?: boolean },
) {
  const { brands } = useBrands(partnerId, options);
  return useMemo(() => {
    if (!brands) return [];
    return brands.map((brand) => ({
      id: brand.brandId,
      value: brand.brandId,
      label: brand.name,
      data: brand,
    }));
  }, [brands]);
}
